import axios from "axios";
import Cookie from "js-cookie";
import { mapFromSection, toDollars, toPercent } from '../../utilities';
import appUrls from '../../../helpers/environment';
import formulaHandlerMixins from './formula-handler-mixins';

export default {
    computed: {
        computedHeaders: function () {
            // Push the actions column onto the provided headers
            // This will allow the edit and delete icons to show
            const { props = {} } = this.item;
            const { headers, tableSubmoduleID } = props;
            const myHeaders = [];

            if (headers) {
                const customHeaders = headers.map(thisHeader => {
                    const { value, useDisplay, format, formula } = thisHeader;

                    return {
                        ...thisHeader,
                        value: (useDisplay || format || formula) ? `${value}-display` : value
                    }
                });

                myHeaders.push(...customHeaders);

                if (tableSubmoduleID) {
                    myHeaders.push({
                        text: '',
                        value: 'submoduleLink',
                        width: '33%'
                    });
                }

                if (this.hasActions) {
                    myHeaders.push({
                        text: '',
                        value: 'action',
                        width: '8%'
                    });
                }
            }

            return myHeaders;
        },
        deleteConfirmMessage: function () {
            return this.item.props.deleteConfirmMessage || 'Are you sure you want to delete this item?';
        },
        disableInsert: function () {
            const maxItems = this.item.props.maxItems;
            const itemLength = this.items ? this.items.length : 0;

            if (maxItems) {
                return itemLength >= maxItems;
            }

            return false;
        },
        
        isMultiple: function () {
            return this.item.props.multiple ? true : false
        },

        items: {
            get: function () {
                let normalizedValue = this.value;

                if (typeof normalizedValue === 'string') {
                    try {
                        normalizedValue = JSON.parse(normalizedValue);
                    } catch (e) {
                        normalizedValue = [];
                    }
                }

                const items = Array.isArray(normalizedValue) ? normalizedValue.slice(0) : [];
                const { headers, displayFields, sortBy, sortDesc } = this.item.props;
                const self = this;

                function setDisplayValues (displayField) {
                    const { value, format, formula, useDisplay } = displayField;

                    if (format) {
                        // For formatted items
                        items.map(thisItem => {
                            if (thisItem[value]) {
                                thisItem[`${value}-display`] = self[format].call(self, thisItem[value]);
                            }
                        });
                    }
                    
                    if (useDisplay) {
                        // For each header with the "useDisplay" property it will pull the value from the select list which matches the ID
                        // The original attribute is not modified, instead a new attribute is created with "-display" appended to the name
                        // and the value is the new display value
                        const thisValuationData =  self.getStructureDataByName(value);
                        const thisValuationDataItems = thisValuationData && thisValuationData.props && thisValuationData.props.items;

                        if (!thisValuationDataItems) {
                            return;
                        }

                        const itemValuationMap = thisValuationDataItems.reduce((memo, thisItemValuation) => {
                            memo[thisItemValuation.value] = thisItemValuation.text;

                            return memo;
                        }, {});

                        items.map(thisItem => {
                            const itemValue = thisItem[value];
                            
                            if (Array.isArray(itemValue)) {
                                thisItem[`${value}-display`] = itemValue.map((thisValue) => itemValuationMap[thisValue]).join(', ');
                            } else {
                                thisItem[`${value}-display`] = itemValuationMap[itemValue];
                            }
                        });
                    }

                    if (formula) {
                        // For items with a formula
                        // Fall back to the formula handlers directly so tables don't depend on including the formula mixin
                        const formulaHandler = self[formula.name] || formulaHandlerMixins.methods[formula.name];

                        if (!formulaHandler) {
                            console.warn(`Unknown formula "${formula.name}" on table column "${value}"`);
                            return;
                        }

                        items.map(thisItem => {
                            thisItem[`${value}-display`] = formulaHandler.call(self, formula.arguments, thisItem);
                        });
                    }
                }

                if (displayFields && items) {
                    displayFields.forEach((thisDisplayField) => setDisplayValues(thisDisplayField));
                }

                if (headers && items) {
                    headers.forEach((thisHeader) => setDisplayValues(thisHeader));
                }

                if (sortBy) {
                    items.sort((a, b) => {
                        const valA = a[sortBy];
                        const valB = b[sortBy];
                        const sortOrder = sortDesc ? -1 : 1;

                        return valA > valB ? sortOrder : valA < valB ? -sortOrder : 0;
                    });
                }

                return items;
            },
            set: function (newVal) {
                this.$emit('input', newVal);
            }
        },
        noDelete: function () {
            // @Cliff remove the isFileUpload check to enable delete - removed from below || this.item.props.isFileUpload
            return this.item.props.noDelete;
        },
        noEdit: function () {
            // disallow edit for uploads
            return this.item.props.noEdit || this.item.props.isFileUpload;
        },
    },
    data: function () {
        return {
            dialog: false,
            editedItem: {},
            editedIndex: -1,
            isNew: true,
            structureData: {},
            isAdmin: ['admin', 'admindev', '8081'].some(el => window.location.origin.includes(el))
        };
    },
    methods: {
        async deleteItem (item, skipConfirm) {
            const index = this.items.indexOf(item);
            const deleteConfirmed = skipConfirm || confirm(this.deleteConfirmMessage);

            if (deleteConfirmed) {
                if (this.item.props.isFileUpload && typeof this.deleteFile === 'function') {
                    await this.deleteFile(item);
                }

                this.items.splice(index, 1);
                this.$emit('input', this.items);
            }
        },

        editItem (item) {
            this.editedIndex = this.items.indexOf(item);
            this.isNew = false;
            this.dialog = true;
        },

        getStructureDataByName (valuationName) {
            if (this.structureData && this.structureData.items) {
                const dataMap = mapFromSection(this.structureData);

                return dataMap[valuationName];
            }
        },

        handleOpenSubmodule (item) {
            const { module_id, module_key, project_id } = item;

            if ( this.isAdmin ) {
                const prj = {
                    module_id: module_id,
                    modinfo: { "module_key": module_key },
                    project_id: project_id
                }
                this.$emit('openQuestionnaire', prj)
            } else {
                window.open(`/module_questionnaire/${module_key}/${project_id}`, '_blank')
            }
        },

        list (value) {
            /**list is used to turn an object into a string of names
             * {
             *      redfish: true,
             *      greenfish: false,
             *      bluefish: true
             * }
             *
             * will return 'redfish, bluefish'
             */
            const entries = Object.entries(value);

            return entries.reduce((memo, thisEntry, index) => {
                const [ key, value ] = thisEntry;

                if (value) {
                    if (index > 0) {
                        memo = memo + ', ';
                    }

                    memo = memo + key;
                }

                return memo;
            }, '');
        },

        dollars (value) {
            return toDollars(value);
        },

        percent (value) {
            return toPercent(value);
        },

        setStructure () {
            // This will fetch the form data on load
            const { structure } = this.item.props;
            
            if (structure) {
                this.structureData = structure;
            }
        },

        close () {
            this.dialog = false;

            setTimeout(() => {
                // Allow time for the dialog to close then clean up
                this.editedIndex = -1;
                this.isNew = true;
            }, 300)
        },
        
        async save (item) {
            const editedItems_obj = Object.entries(item).map(([key, item]) => item );
            const firstEditedItem = editedItems_obj[0];
            const isFileUpload = firstEditedItem && firstEditedItem.hasOwnProperty('fileid') ? true : false;

            if (isFileUpload) {
                // Handle file upload
                const file_items = editedItems_obj.length ? editedItems_obj : []
                
                // MERGE NEW FILES INTO EXISTING FILES
                const merged_files = [...this.items, ...file_items];
                // UPDATE THE STORE                        
                this.$emit('input', merged_files);
            } else if (this.editedIndex > -1 && this.items.length) {
                // Item already exists, this was an edit                   
                Object.assign(this.items[this.editedIndex], item);
                
                // UPDATE THE STORE
                this.$emit('input', this.items);
            } else {
                // New item
                if (!this.items) {
                    this.items = [];
                }
                

                if (this.item.props.tableSubmoduleID) {
                    // Save the submodule
                    const { module_id, module_key, project_id } = await this.saveSubmodule({
                        submodule_id: this.item.props.tableSubmoduleID,
                        project_id: this.projectId,
                    });

                    // Add the submodule data to the item
                    Object.assign(item, {
                        module_id,
                        module_key,
                        project_id
                    });
                }

                // This is a new item, add it to the list
                this.items.push(item);
                
                // UPDATE THE STORE
                this.$emit('input', this.items);
            }

            this.close();
        },

        async saveSubmodule (data) {
            const { submodule_id, project_id } = data;

            const response = await axios.post(`${appUrls.api}/api/add_submodule`, {
                headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                data: {
                    module: {
                        submodule_id,
                        project_id
                    }
                }
            });

            return response.data;
        },

        validate: function (value) {
            return this.$refs.input && this.$refs.input.validate(false, value);
        }
    },

    watch: {
        editedIndex: function (newVal, oldVal) {
            if (newVal > -1) {
                this.editedItem = Object.assign({}, this.items[newVal]);
            } else {
                this.editedItem = {};
            }
        }
    }
};


// WEBPACK FOOTER //
// ./src/components/questionnaire_template/mixins/data-table-mixins.js